/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `AppointmentRequest` for the Caintra Connect frontend.
 * @module pages/AppointmentRequest
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../utils/logger.js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { getRequirementFieldValue } from "../utils/common.js";
import AppointmentRequestView from "./AppointmentRequestView.jsx";
import {
  VIEW_APPOINTMENTS_PATH,
  approveAppointmentRequest,
  confirmAppointmentSchedule,
  likeAppointmentRequest,
  rejectAppointmentRequest,
} from "./appointmentRequestActions.js";

const REQUIRED_FIELD_VALUE = 2;

const isEmptyRequestValue = (value) =>
  !value || value === "" || value === null || value === undefined;

const assignIfPresent = (value, setter) => {
  if (value) {
    setter(value);
  }
};

const getFieldValidation = (requirement, request, fieldKey) => {
  if (!requirement?.requisitos) {
    return null;
  }
  const fieldRequirement = requirement.requisitos.find(
    (req) => req.key === fieldKey,
  );
  if (fieldRequirement?.value !== REQUIRED_FIELD_VALUE) {
    return false;
  }
  return isEmptyRequestValue(
    getRequirementFieldValue(request, fieldKey, undefined, 0),
  );
};

const hasValidationErrors = (requirement, request) => {
  if (!requirement?.requisitos || !request) {
    return false;
  }
  return requirement.requisitos.some((req) => {
    if (req.value !== REQUIRED_FIELD_VALUE) {
      return false;
    }
    return isEmptyRequestValue(
      getRequirementFieldValue(request, req.key, undefined, 0),
    );
  });
};

const applyLocationState = (locationState, actions) => {
  logger.info("location.state received in AppointmentRequest: ", {
    data: locationState,
  });

  if (locationState.request) {
    actions.setRequest(locationState.request);
    actions.setIsLiked(locationState.request.meGusta === true);
  }

  assignIfPresent(locationState.appointments, actions.setAppointments);
  assignIfPresent(locationState.requirement, actions.setRequirement);
  assignIfPresent(locationState.eventData, actions.setEventData);
  assignIfPresent(
    locationState.participatingCompanyData,
    actions.setParticipatingCompanyData,
  );

  if (locationState.user) {
    actions.setUser(locationState.user);
    logger.info("User data received from state:", {
      data: locationState.user,
    });
  }
};

const AppointmentRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [requirement, setRequirement] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [user, setUser] = useState(null);
  const [participatingCompanyData, setParticipatingCompanyData] =
    useState(null);

  const validateField = useCallback(
    (fieldKey) => getFieldValidation(requirement, request, fieldKey),
    [requirement, request],
  );

  useEffect(() => {
    const setAppointmentRequestData = async () => {
      if (!location.state) {
        navigate(VIEW_APPOINTMENTS_PATH);
        return;
      }
      applyLocationState(location.state, {
        setRequest,
        setIsLiked,
        setAppointments,
        setRequirement,
        setEventData,
        setParticipatingCompanyData,
        setUser,
      });
      setLoading(false);
    };

    setAppointmentRequestData();
  }, [location.state, navigate]);

  const handleBack = useCallback(() => {
    logger.info("Navigating back with eventData:", { data: eventData });
    navigate(VIEW_APPOINTMENTS_PATH, { state: { event: eventData } });
  }, [eventData, navigate]);

  const handleRejectRequest = useCallback(
    () =>
      rejectAppointmentRequest({
        request,
        navigate,
        eventData,
        setIsProcessing,
      }),
    [request, navigate, eventData],
  );

  const handleApproveRequest = useCallback(
    () =>
      approveAppointmentRequest({
        request,
        navigate,
        setIsProcessing,
        setIsScheduleModalOpen,
      }),
    [request, navigate],
  );

  const handleLikeRequest = useCallback(
    () =>
      likeAppointmentRequest({
        request,
        navigate,
        setIsProcessing,
        setIsLiked,
        setRequest,
      }),
    [request, navigate],
  );

  const handleScheduleModalClose = useCallback(() => {
    setIsScheduleModalOpen(false);
  }, []);

  const handleScheduleConfirm = useCallback(
    (appointmentInfo) =>
      confirmAppointmentSchedule({
        request,
        navigate,
        eventData,
        appointmentInfo,
        setIsProcessing,
        setIsScheduleModalOpen,
      }),
    [request, navigate, eventData],
  );

  return (
    <AppointmentRequestView
      navigate={navigate}
      loading={loading}
      request={request}
      showValidationWarning={hasValidationErrors(requirement, request)}
      user={user}
      validateField={validateField}
      isProcessing={isProcessing}
      isLiked={isLiked}
      isScheduleModalOpen={isScheduleModalOpen}
      eventData={eventData}
      participatingCompanyData={participatingCompanyData}
      appointments={appointments}
      handleBack={handleBack}
      handleRejectRequest={handleRejectRequest}
      handleApproveRequest={handleApproveRequest}
      handleLikeRequest={handleLikeRequest}
      handleScheduleModalClose={handleScheduleModalClose}
      handleScheduleConfirm={handleScheduleConfirm}
    />
  );
};

export default AppointmentRequest;
